'use client';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { useAccount, useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { getAgentName, getCurrentDateTimeCST } from '@/helpers/utils/siteUtils';
import { AddNotePayload, CustomerNotesPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import {
  ADDITIONAL_DETAILS_NAME,
  ADDITIONAL_DETAILS_TYPE,
  NOTES_FLAG_FOR_PAPERWORK,
} from '../../../../../constants/constant';
import { getNoteTemplate, NOTE_TYPES, PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE } from '../../customer-detail/constants';
import { transformNotes } from '../../customer-detail/utils';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTrigger, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { Textarea } from '../../ui/textarea';
import { useToast } from '../../ui/use-toast';

const NoteModal = ({
  Texts,
  flag,
  noteOptions,
  setAllCustomerNotes,
  setCustomerNotes,
  type,
  noteNumber,
  setTotalItems,
  addNewNotes,
  isCustomerDetailsPage = false,
  isViewAll = false,
}: {
  Texts: any;
  flag?: boolean;
  noteOptions: any;
  addNewNotes?: any;
  setAllCustomerNotes?: any;
  setCustomerNotes?: any;
  type?: string;
  noteNumber?: number;
  setTotalItems?: any;
  isCustomerDetailsPage?: boolean;
  isViewAll?: boolean;
}) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const params = useSearchParams();
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedNote, setSelectedNote] = useState(NOTE_TYPES.CUSTOM_NOTE);
  const { addNote, addOrderNote, getCustomerNotes } = useAccount();
  const { addAdditionalDetails } = useCart(false);
  const projectSettings: ProjectSettings = useProjSettings();
  const { allowedPermissionsForAgent } = projectSettings;
  const [agentName] = useState(getAgentName().replace(/@.*/, ''));
  const [isPriorityChecked, setIsPriorityChecked] = useState(false);

  const handleAddNote = async () => {
    if (note === '') {
      setError(Texts.enterNote);
      return;
    }
    setLoading(true);
    if (type) {
      const payload = {
        name: `${ADDITIONAL_DETAILS_NAME.ADD_NOTE}-${noteNumber}`, // cart add notes
        value: note,
        type: ADDITIONAL_DETAILS_TYPE.NOTES,
        agentName,
      };
      await addAdditionalDetails(payload);
      SuccessNotification(toast, { description: Texts?.addNoteSuccess, action: <SuccessIcon className="h-5 w-5" /> });
      setOpen(false);
      return setLoading(false);
    }
    if (flag) {
      const currentDate = getCurrentDateTimeCST();
      const payload = {
        data: {
          Note: {
            createdBy: agentName,
            createdDate: currentDate,
            isPriority: isPriorityChecked,
            text: note,
          },
        },
        query: {
          orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
          divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        },
      };

      await addOrderNote(payload).then((response) => {
        setLoading(false);
        setIsPriorityChecked(false);
        if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
          setOpen(false);
          addNewNotes();
          SuccessNotification(toast, {
            description: Texts?.addNoteSuccess,
            action: <SuccessIcon className="h-5 w-5" />,
          });
        } else {
          ErrorNotification(toast, { description: Texts?.addNoteError, action: <ErrorIcon className="h-5 w-5" /> });
        }
      });
    } else {
      const payload = {
        query: {
          customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID),
          divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
          X_INITIATING_AGENT: agentName,
        },
        data: { note: note },
      } as AddNotePayload;
      await addNote(payload)
        .then((res) => {
          setLoading(false);
          if (res?.code === RESPONSE_SUCCESS_MESSAGE) {
            getCustomersNotes();
            setTotalItems((prev: number) => prev + 1);
            SuccessNotification(toast, {
              description: Texts?.addNoteSuccess,
              action: <SuccessIcon className="h-5 w-5" />,
            });
          } else {
            setLoading(false);
            ErrorNotification(toast, { description: Texts?.addNoteError, action: <ErrorIcon className="h-5 w-5" /> });
          }
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  const getCustomersNotes = async () => {
    const payload = {
      omsCustomerId: params.get(PAYLOAD_QUERY.OMS_CUSTOMER_ID),
      customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID),
      divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      pageSize: isCustomerDetailsPage ? 100 : 5,
    } as CustomerNotesPayload;
    const notes = await getCustomerNotes(payload);
    const transformNoteToData = transformNotes(notes.Notes || []);
    if (isViewAll) {
      setAllCustomerNotes?.(transformNoteToData);
    }
    setCustomerNotes?.(transformNoteToData?.slice(0, 5));
    setLoading(false);
    setOpen(false);
  };

  useEffect(() => {
    if (note !== '') {
      setError('');
    }
  }, [note]);

  const combinedKeyPressHandler = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      !/^[a-z0-9\s.,;:!?'"()\-]+$/i.test(event.key) &&
      event.key !== 'Backspace' &&
      event.key !== 'Tab' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    ) {
      event.preventDefault();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (value) {
          setNote('');
          setSelectedNote(NOTE_TYPES.CUSTOM_NOTE);
        }
        setOpen(value);
      }}
    >
      <DialogTrigger className="h-[30px] rounded-md border-[1px] border-foggyGray px-4 py-1.5 text-xs font-medium">
        {Texts.addNewNotes}
      </DialogTrigger>

      <DialogContent className="max-w-[690px] bg-white">
        {loading && <Loader />}
        <DialogHeader>
          {/* chng-text-25px */}
          <DialogTitle className="text-2xl font-bold">{Texts.addNotes}</DialogTitle>
        </DialogHeader>
        <section className={`flex-col-2 flex ${!type ? 'h-80' : 'h-auto'} gap-4`}>
          {!type && (
            <section className=" custom-scrollbar  w-1/2 overflow-y-auto rounded-md bg-lightSilver p-4">
              <div>
                {noteOptions?.map((noteTitle: string) => (
                  <button
                    key={`key-${noteTitle}`}
                    className={`mb-1 block w-full cursor-pointer px-2 py-3 text-left text-base hover:bg-silverGray ${
                      noteTitle?.includes(NOTE_TYPES.CUSTOM_NOTE) ? 'font-bold' : 'font-normal'
                    }`}
                    onClick={() => {
                      const noteTemplate = getNoteTemplate(noteTitle);
                      setNote(noteTemplate);
                      setSelectedNote(noteTemplate);
                    }}
                  >
                    {noteTitle}
                  </button>
                ))}
              </div>
            </section>
          )}
          <section className={`${!type ? 'w-1/2' : 'h-[150px] w-full'} `}>
            <Textarea
              placeholder="Your Notes Here..."
              className="h-full w-full resize-none font-bold"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
              maxLength={2500}
              onKeyPress={combinedKeyPressHandler}
            />
          </section>
        </section>
        {allowedPermissionsForAgent?.includes(NOTES_FLAG_FOR_PAPERWORK) && flag && (
          <p>
            <input
              type="checkbox"
              id="flagPaperwork"
              aria-labelledby="Checkbox for Flag for Paperwork"
              className="custom-checkbox focus:shadow:none mr-1 h-6 w-6 shadow-none hover:cursor-pointer focus:outline-none focus:ring-0 lg:h-4 lg:w-4"
              tabIndex={0}
              checked={isPriorityChecked}
              onChange={() => setIsPriorityChecked(!isPriorityChecked)}
            />{' '}
            <label
              htmlFor="flagPaperwork"
              className="text-base font-semibold leading-[19.4px] text-black hover:cursor-pointer lg:text-sm"
              tabIndex={0}
            >
              Flag for Paperwork
            </label>
          </p>
        )}
        {error !== '' ? <p className="text-scarletRed">{error}</p> : <></>}
        <section className="mt-4 flex justify-start gap-4">
          <Button className="rounded-md bg-deepBlue p-4 text-white" onClick={() => handleAddNote()} type="submit">
            {Texts.addNoteButton}
          </Button>
          <Button
            className="p-0 underline"
            onClick={() => {
              setOpen(false);
            }}
            type="reset"
          >
            {Texts.cancelButton}
          </Button>
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default NoteModal;
